import * as Headless from "@headlessui/react";
import clsx from "clsx";
import { InfoIcon } from "lucide-react";
import type React from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/Tooltip";

export function Fieldset({
	className,
	...props
}: { className?: string } & Omit<Headless.FieldsetProps, "as" | "className">) {
	return (
		<Headless.Fieldset
			{...props}
			className={clsx(
				className,
				"*:data-[slot=text]:mt-1 [&>*+[data-slot=control]]:mt-6",
			)}
		/>
	);
}

export function Legend({
	className,
	...props
}: { className?: string } & Omit<Headless.LegendProps, "as" | "className">) {
	return (
		<Headless.Legend
			data-slot="legend"
			{...props}
			className={clsx(
				className,
				"text-base/6 font-semibold text-base-content data-disabled:opacity-50 sm:text-sm/6",
			)}
		/>
	);
}

export function FieldGroup({
	className,
	...props
}: React.ComponentPropsWithoutRef<"div">) {
	return (
		<div
			data-slot="control"
			{...props}
			className={clsx(className, "space-y-8")}
		/>
	);
}

export function Field({
	className,
	...props
}: { className?: string } & Omit<Headless.FieldProps, "as" | "className">) {
	return (
		<Headless.Field
			{...props}
			className={clsx(
				className,
				"[&>[data-slot=label]+[data-slot=control]]:mt-3",
				"[&>[data-slot=label]+[data-slot=description]]:mt-1",
				"[&>[data-slot=description]+[data-slot=control]]:mt-3",
				"[&>[data-slot=control]+[data-slot=description]]:mt-3",
				"[&>[data-slot=control]+[data-slot=error]]:mt-3",
				"*:data-[slot=label]:font-medium",
			)}
		/>
	);
}

export function Label({
	className,
	...props
}: { className?: string } & Omit<Headless.LabelProps, "as" | "className">) {
	return (
		<Headless.Label
			data-slot="label"
			{...props}
			className={clsx(
				className,
				"text-base/6 text-base-content select-none data-disabled:opacity-50 sm:text-sm/6",
			)}
		/>
	);
}

export function InfoLabel({
	className,
	info,
	children,
	...props
}: {
	className?: string;
	info: React.ReactNode;
	children: React.ReactNode;
} & Omit<Headless.LabelProps, "as" | "className" | "children">) {
	return (
		<Headless.Label
			data-slot="label"
			{...props}
			className={clsx(
				className,
				"text-base/6 text-base-content select-none data-disabled:opacity-50 sm:text-sm/6 flex items-center gap-1.5",
			)}
		>
			{children}
			<Tooltip>
				<TooltipTrigger className="text-base-content/50 hover:text-base-content transition-colors focus-visible:outline-none">
					<InfoIcon className="size-4" />
				</TooltipTrigger>
				<TooltipContent className="max-w-xs">{info}</TooltipContent>
			</Tooltip>
		</Headless.Label>
	);
}

export function Description({
	className,
	...props
}: { className?: string } & Omit<
	Headless.DescriptionProps,
	"as" | "className"
>) {
	return (
		<Headless.Description
			data-slot="description"
			{...props}
			className={clsx(
				className,
				"text-base/6 text-base-content/50 data-disabled:opacity-50 sm:text-sm/6",
			)}
		/>
	);
}

export function ErrorMessage({
	className,
	...props
}: { className?: string } & Omit<
	Headless.DescriptionProps,
	"as" | "className"
>) {
	return (
		<Headless.Description
			data-slot="error"
			{...props}
			className={clsx(
				className,
				"text-base/6 text-error data-disabled:opacity-50 sm:text-sm/6",
			)}
		/>
	);
}
